import { ProductEntry } from '../../../store/api';
import {
    SubscriptionRenewalLineUpdateRequest,
    UpdateLineItemsParams
} from '../../../api/subscription-management/renewals/updateRenewalLine';
import {
    ExtendedRenewalIndividualNextYearRenewalLine,
    RenewalLineView,
    RenewalType,
    SimpleRenewalLine
} from '../utils/renewalTableTileTypes';
import { formatUsageLimit } from '../../../shared/utils/formatUsageLimit';
import { mapRenewalLine } from './mapRenewalLine';

export const mapNextYearRenewalLine = (
    entry: ProductEntry,
    spocCurrency: string,
    updateRenewalLine: (params: UpdateLineItemsParams) => void
): SimpleRenewalLine => {
    const { renewal } = entry;
    const selectedType = renewal?.types.find(({ selected }) => selected);

    const onRenewalLineUpdate = (params: SubscriptionRenewalLineUpdateRequest) => {
        return updateRenewalLine({
            ...params,
            productId: entry.product.id!
        });
    };

    switch (selectedType?.type) {
        case 'RECOMMENDED':
            return {
                ...mapRenewalLine(renewal!, spocCurrency, onRenewalLineUpdate),
                selectedType: RenewalType.RECOMMENDED,
                type: RenewalLineView.RenewalLine
            };
        case 'SAME_AS_CURRENT_YEAR':
            return {
                ...mapRenewalLine(renewal!, spocCurrency, onRenewalLineUpdate),
                selectedType: RenewalType.SAME_AS_CURRENT_YEAR,
                type: RenewalLineView.RenewalLine
            };
        case 'CUSTOM':
            return {
                ...mapRenewalLine(renewal!, spocCurrency, onRenewalLineUpdate),
                selectedType: RenewalType.CUSTOM,
                type: RenewalLineView.RenewalLine
            };
        case 'DONT_RENEW':
            return {
                ...mapRenewalLine(renewal!, spocCurrency, onRenewalLineUpdate),
                selectedType: RenewalType.DONT_RENEW,
                type: RenewalLineView.RenewalLine
            };
        case 'ALREADY_RENEWED':
            return {
                type: RenewalLineView.Renewed,
                selectedType: RenewalType.ALREADY_RENEWED,
                label: formatUsageLimit(entry.nextSubscriptions?.individualUsageLimit)
            };
        default:
            return {
                type: RenewalLineView.Unavailable,
                selectedType: RenewalType.UNAVAILABLE
            };
    }
};

export const mapExtendedNextYearRenewalLine = (
    entry: ProductEntry,
    spocCurrency: string,
    updateRenewalLine: (params: UpdateLineItemsParams) => void
): ExtendedRenewalIndividualNextYearRenewalLine => {
    const { renewal } = entry;
    const selectedType = renewal?.types.find(({ selected }) => selected);

    const onRenewalLineUpdate = (params: SubscriptionRenewalLineUpdateRequest) => {
        return updateRenewalLine({
            ...params,
            productId: entry.product.id!
        });
    };

    const hasSameAsCurrentYearType = renewal?.types.find(({ type }) => type === 'SAME_AS_CURRENT_YEAR');
    const recommendationType = renewal?.types.find(({ type }) => type === 'RECOMMENDED');
    const recommendationHasNoUsageLimit = !recommendationType?.usageLimit;

    if (!hasSameAsCurrentYearType && recommendationHasNoUsageLimit) {
        switch (selectedType?.type) {
            case 'RECOMMENDED':
                return {
                    ...mapRenewalLine(renewal!, spocCurrency, onRenewalLineUpdate),
                    selectedType: RenewalType.RECOMMENDED,
                    type: RenewalLineView.Adapted_Recommended
                };
            case 'CUSTOM':
                return {
                    ...mapRenewalLine(renewal!, spocCurrency, onRenewalLineUpdate),
                    selectedType: RenewalType.CUSTOM,
                    type: RenewalLineView.Adapted_Custom
                };
        }
    }

    return mapNextYearRenewalLine(entry, spocCurrency, updateRenewalLine);
};
